import React from "react";
import { Button, Popconfirm, Space, Table, Tag, Tooltip, Typography, type TableProps } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { SalesMeetingSaleStatus, type SalesMeetingStatus } from "@fitness-app/data-models/entities/SalesMeeting";

import { type ToggleableColumn } from "~/hooks/useColumnVisibility";

import { meetingStatusLabels, saleStatusLabels, saleStatusTableFilters, statusColor } from "../salesCrmConstants";
import { type SalesMeetingCapabilities } from "../salesCrmAccess";
import {
  formatMoney,
  getBundleClientPath,
  getConfirmedSaleBundle,
  getMeetingBundle,
  isManualMeeting,
} from "../salesCrmUtils";
import { type SaleStatusFilterValue, type SalesMeetingRow } from "../hooks/useSalesMeetingsQuery";
import { type PendingManualOrder } from "../hooks/usePendingManualOrdersQuery";
import { type BlacklistPrefill } from "../SalesBlacklist";
import { CopySmsMenu, SalesSmsStatusMarkers } from "../SalesSmsStatus";

// "Klient" and the actions column stay always visible (TRE-2398).
export const salesMeetingsColumns = [
  { key: "startTime", label: "Data" },
  { key: "seller", label: "Sprzedawca" },
  { key: "status", label: "Status" },
  { key: "saleStatus", label: "Status sprzedaży" },
  { key: "sms", label: "Statusy SMS" },
  { key: "cartPayment", label: "Płatność w koszyku" },
  { key: "paid", label: "Opłacone" },
  { key: "contracted", label: "Zakontraktowane" },
] as const satisfies readonly ToggleableColumn[];

type SalesMeetingsColumnKey = (typeof salesMeetingsColumns)[number]["key"];

type SalesMeetingsPagination = {
  count: number;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: string[];
  setCurrentPage: (page: number) => void;
  showSizeChanger: boolean;
};

type SalesMeetingsTableProps = {
  access: {
    getMeetingCapabilities: (memberId: string | null) => SalesMeetingCapabilities;
  };
  isColumnVisible: (key: SalesMeetingsColumnKey) => boolean;
  loading: boolean;
  meetings: SalesMeetingRow[];
  onBlacklist: (prefill: BlacklistPrefill) => void;
  onConfirmManualPayment: (order: PendingManualOrder) => void;
  confirmManualPaymentLoading: boolean;
  /** Zamówienia manualne „do potwierdzenia" atrybuowane do zalogowanego sprzedawcy (TRE-2006). */
  pendingManualOrdersByEmail: ReadonlyMap<string, PendingManualOrder>;
  onEdit: (meeting: SalesMeetingRow) => void;
  onSaleStatusFiltersChange: (filters: SaleStatusFilterValue[]) => void;
  pagination: SalesMeetingsPagination;
  selectedSaleStatusFilters: SaleStatusFilterValue[];
};

export const SalesMeetingsTable = ({
  access,
  isColumnVisible,
  loading,
  meetings,
  onBlacklist,
  onConfirmManualPayment,
  confirmManualPaymentLoading,
  pendingManualOrdersByEmail,
  onEdit,
  onSaleStatusFiltersChange,
  pagination,
  selectedSaleStatusFilters,
}: SalesMeetingsTableProps): React.ReactElement => {
  const navigate = useNavigate();

  const handleChange: TableProps<SalesMeetingRow>["onChange"] = (nextPagination, filters) => {
    const nextSaleStatusFilters = (filters.saleStatus ?? []) as SaleStatusFilterValue[];
    const filtersChanged = nextSaleStatusFilters.join("|") !== selectedSaleStatusFilters.join("|");

    if (filtersChanged) {
      pagination.setCurrentPage(1);
      onSaleStatusFiltersChange(nextSaleStatusFilters);
      return;
    }

    if (nextPagination.current && nextPagination.current !== pagination.currentPage) {
      pagination.setCurrentPage(nextPagination.current);
    }
  };

  return (
    <Table<SalesMeetingRow>
      rowKey="id"
      dataSource={meetings}
      loading={loading}
      onChange={handleChange}
      pagination={{
        current: pagination.currentPage,
        pageSize: pagination.pageSize,
        total: pagination.count,
        pageSizeOptions: pagination.pageSizeOptions,
        showSizeChanger: pagination.showSizeChanger,
      }}
      scroll={{ x: true }}
    >
      <Table.Column<SalesMeetingRow>
        title="Data"
        hidden={!isColumnVisible("startTime")}
        dataIndex="startTime"
        render={(value) => dayjs(value).format("DD.MM.YYYY HH:mm")}
      />
      <Table.Column<SalesMeetingRow>
        title="Klient"
        render={(_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text>{row.clientName ?? "-"}</Typography.Text>
            <Typography.Text type="secondary">{row.clientEmail}</Typography.Text>
          </Space>
        )}
      />
      <Table.Column<SalesMeetingRow>
        title="Sprzedawca"
        hidden={!isColumnVisible("seller")}
        render={(_, row) =>
          row.teamMember ? `${row.teamMember.firstName} ${row.teamMember.lastName}` : (row.sellerName ?? "-")
        }
      />
      <Table.Column<SalesMeetingRow>
        title="Status"
        dataIndex="status"
        hidden={!isColumnVisible("status")}
        render={(value: SalesMeetingStatus, row) => {
          const tag = <Tag color={statusColor[value] ?? "default"}>{meetingStatusLabels[value]}</Tag>;
          const statusTag =
            row.matchConfidence === "purchase_auto" ? (
              <Tooltip title="Status skorygowany automatycznie po dopasowaniu zakupu">{tag}</Tooltip>
            ) : (
              tag
            );

          if (!isManualMeeting(row)) return statusTag;

          return (
            <Space size={4} wrap>
              {statusTag}
              <Tooltip title="Spotkanie dodane ręcznie — rozmowa odbyła się poza Calendly">
                <Tag color="purple">Dodane ręcznie</Tag>
              </Tooltip>
            </Space>
          );
        }}
      />
      <Table.Column<SalesMeetingRow>
        title="Status sprzedaży"
        key="saleStatus"
        hidden={!isColumnVisible("saleStatus")}
        dataIndex="saleStatus"
        filters={saleStatusTableFilters}
        filteredValue={selectedSaleStatusFilters}
        render={(value, row) => (
          <Space size={4} wrap>
            {value ? (
              <Tooltip title={value === SalesMeetingSaleStatus.NotQualified ? row.notQualifiedReason : null}>
                <Tag color={value === SalesMeetingSaleStatus.Yes ? "green" : "default"}>
                  {saleStatusLabels[value as SalesMeetingSaleStatus]}
                </Tag>
              </Tooltip>
            ) : (
              "-"
            )}
            {row.postWarmupWithdrawalAt ? (
              <Tooltip
                title={`Wycofanie ${dayjs(row.postWarmupWithdrawalAt).format(
                  "DD.MM.YYYY",
                )} — po zakończeniu warmupu, prowizja zostaje`}
              >
                <Tag color="orange">Wycofany po warmupie</Tag>
              </Tooltip>
            ) : null}
          </Space>
        )}
      />
      <Table.Column<SalesMeetingRow>
        title="Statusy SMS"
        hidden={!isColumnVisible("sms")}
        render={(_, row) => (
          <SalesSmsStatusMarkers capabilities={access.getMeetingCapabilities(row.teamMemberId)} meeting={row} />
        )}
      />
      <Table.Column<SalesMeetingRow>
        title="Płatność w koszyku"
        hidden={!isColumnVisible("cartPayment")}
        render={(_, row) => {
          const bundle = getMeetingBundle(row);
          const bundleClientPath = bundle ? getBundleClientPath(bundle, row.clientEmail) : null;
          const pendingOrder = pendingManualOrdersByEmail.get(row.clientEmail?.trim().toLowerCase() ?? "");

          const bundleCell = !bundle ? null : bundleClientPath ? (
            <Typography.Link onClick={() => navigate(bundleClientPath)}>{bundle.name}</Typography.Link>
          ) : (
            bundle.name
          );

          if (!pendingOrder) return bundleCell ?? "-";

          return (
            <Space direction="vertical" size={4}>
              {bundleCell}
              <Tooltip title={`${pendingOrder.name ?? "Zamówienie"} — przelew oczekuje na potwierdzenie`}>
                <Tag color="orange">
                  Przelew {formatMoney(pendingOrder.amount, pendingOrder.currency ?? "PLN")} do potwierdzenia
                </Tag>
              </Tooltip>
              <Popconfirm
                title="Potwierdzić wpłatę przelewem?"
                description="Dokument fiskalny wystawi księgowość, gdy przelew zostanie zaksięgowany na koncie."
                okText="Potwierdź"
                cancelText="Anuluj"
                onConfirm={() => onConfirmManualPayment(pendingOrder)}
              >
                <Button size="small" type="primary" loading={confirmManualPaymentLoading}>
                  Potwierdź wpłatę
                </Button>
              </Popconfirm>
            </Space>
          );
        }}
      />
      <Table.Column<SalesMeetingRow>
        title="Opłacone"
        hidden={!isColumnVisible("paid")}
        render={(_, row) => formatMoney(getConfirmedSaleBundle(row)?.amount, getConfirmedSaleBundle(row)?.currency)}
      />
      <Table.Column<SalesMeetingRow>
        title="Zakontraktowane"
        hidden={!isColumnVisible("contracted")}
        render={(_, row) =>
          formatMoney(getConfirmedSaleBundle(row)?.totalAmount, getConfirmedSaleBundle(row)?.currency)
        }
      />
      <Table.Column<SalesMeetingRow>
        title=""
        render={(_, row) => {
          const capabilities = access.getMeetingCapabilities(row.teamMemberId);

          return (
            <Space>
              <CopySmsMenu capabilities={capabilities} meeting={row} />
              {capabilities.canUpdate ? <Button onClick={() => onEdit(row)}>Edytuj</Button> : null}
              {capabilities.canBlacklist ? (
                <Button danger onClick={() => onBlacklist({ email: row.clientEmail, phone: row.clientPhone })}>
                  Zablokuj
                </Button>
              ) : null}
            </Space>
          );
        }}
      />
    </Table>
  );
};
